import React, { useState, useEffect, useCallback } from 'react';
import {
  getAccountingReview, approvePettyCashTransaction, rejectPettyCashTransaction,
  flagJournalEntry,
} from '../api';
import { formatCurrency } from '../utils/currency';

function AccountingReview({ authAxios, showFormMessage }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [flagModal, setFlagModal] = useState(null); // { id, is_flagged, reason }

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const d = await getAccountingReview(authAxios);
      setData(d);
    } catch { showFormMessage('error', 'Failed to load review data.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (txnId) => {
    try {
      await approvePettyCashTransaction(authAxios, txnId);
      showFormMessage('success', 'Transaction approved.');
      fetchData();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to approve.'); }
  };

  const handleReject = async (txnId) => {
    try {
      await rejectPettyCashTransaction(authAxios, txnId);
      showFormMessage('success', 'Transaction rejected.');
      fetchData();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to reject.'); }
  };

  const handleFlag = async () => {
    if (!flagModal) return;
    try {
      await flagJournalEntry(authAxios, flagModal.id, {
        is_flagged: !flagModal.is_flagged,
        reason: flagModal.reason || '',
      });
      showFormMessage('success', flagModal.is_flagged ? 'Flag removed.' : 'Journal flagged.');
      setFlagModal(null);
      fetchData();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to flag.'); }
  };

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Accounting Review</h2>
        <p className="text-gray-500 text-sm mt-1">Pending approvals & flagged journal entries</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-500 uppercase">Pending Petty Cash</p>
          <p className="text-2xl font-bold text-amber-600">{data.pending_pc_count || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-500 uppercase">Flagged Journals</p>
          <p className="text-2xl font-bold text-red-600">{data.flagged_journal_count || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-500 uppercase">Total Items</p>
          <p className="text-2xl font-bold text-gray-800">{(data.pending_pc_count || 0) + (data.flagged_journal_count || 0)}</p>
        </div>
      </div>

      {/* Pending Petty Cash Transactions */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Pending Petty Cash Transactions</h3>
        {(!data.pending_petty_cash || data.pending_petty_cash.length === 0) ? (
          <div className="text-center py-6 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500 text-sm">No pending transactions.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fund</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.pending_petty_cash.map(t => (
                  <tr key={t.id} className="hover:bg-amber-50">
                    <td className="px-4 py-3 text-gray-700">{t.date}</td>
                    <td className="px-4 py-3 text-gray-600">{t.fund_name || `Fund #${t.fund}`}</td>
                    <td className="px-4 py-3 text-gray-800">{t.description}</td>
                    <td className="px-4 py-3 text-gray-600">{t.category_display || t.category}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(t.amount || 0)}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => handleApprove(t.id)} className="text-green-600 hover:text-green-800 text-xs font-medium">Approve</button>
                      <button onClick={() => handleReject(t.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Flagged Journal Entries */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Flagged Journal Entries</h3>
        {(!data.flagged_journals || data.flagged_journals.length === 0) ? (
          <div className="text-center py-6 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500 text-sm">No flagged journal entries.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag Reason</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.flagged_journals.map(j => (
                  <tr key={j.id} className="hover:bg-red-50">
                    <td className="px-4 py-3 text-gray-700">{j.date}</td>
                    <td className="px-4 py-3 text-gray-800">{j.description}</td>
                    <td className="px-4 py-3 text-gray-600">{j.account_name || `#${j.account}`}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(j.debit_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(j.credit_amount || 0)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{j.flag_reason || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setFlagModal({ id: j.id, is_flagged: true, reason: '' })}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">Unflag</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Flag / Unflag Modal */}
      {flagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-4">
              {flagModal.is_flagged ? 'Remove Flag' : 'Flag Journal Entry'}
            </h3>
            <div className="space-y-4">
              {!flagModal.is_flagged && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea value={flagModal.reason} onChange={e => setFlagModal(f => ({ ...f, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={3} placeholder="Reason for flagging this entry..." />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setFlagModal(null)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={handleFlag} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
                  {flagModal.is_flagged ? 'Remove Flag' : 'Flag Entry'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountingReview;
